import withEllipsis from '@material-ui/core/utils/withEllipsis';
import * as React from 'react';
import Chip from '@material-ui/core/Chip';
import {StandardProps} from '@material-ui/core';
import * as classNames from 'classnames';
import InputLabel from '@material-ui/core/InputLabel';
import {useState, useCallback, useMemo} from 'react';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import * as _ from 'lodash';
import {FormField, ExtendedWrappedFieldProps} from '../FormFields';
import {FormSelectOption, FormSelectOptions} from '../utils/orderByUtils';
import Input, {ExtendedInputProps} from '@material-ui/core/Input';
import {Theme} from '@material-ui/core/styles';
import Popper from '@material-ui/core/Popper';
import Paper from '@material-ui/core/Paper';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import CloseIcon from '@material-ui/icons/Close';
import RootRef from '@material-ui/core/RootRef';
import {List, AutoSizer} from 'react-virtualized';
import Select, {Creatable} from 'react-select';
import {makeStyles, useTheme, createStyles} from '@material-ui/styles';
import Typography from '@material-ui/core/Typography';
import {SelectComponents} from 'react-select/lib/components';
import {IndicatorProps} from 'react-select/lib/components/indicators';
import {ControlProps} from 'react-select/lib/components/Control';
import {NoticeProps, MenuPortalProps, MenuProps, MenuListProps} from 'react-select/lib/components/Menu';
import {OptionProps} from 'react-select/lib/components/Option';
import {PlaceholderProps} from 'react-select/lib/components/Placeholder';
import {State, Props as SelectProps} from 'react-select/lib/Select';
import {StylesConfig} from 'react-select/lib/styles';
import {SingleValueProps} from 'react-select/lib/components/SingleValue';
import {MultiValueProps} from 'react-select/lib/components/MultiValue';
import CancelIcon from '@material-ui/icons/Cancel';
import {emphasize} from '@material-ui/core/styles/colorManipulator';
import {ValueContainerProps} from 'react-select/lib/components/containers';
import {CreatableProps} from 'react-select/lib/Creatable';
// @ts-ignore // todo this import should change when switching to newer version of MUI, version 4.6 has useForkRef in separate file.
import {useForkRef} from '@material-ui/core/utils/reactHelpers';
import {ListItemText, ListSubheader, ListItemIcon, ListItem, Divider} from '@material-ui/core/index';

export enum MIN_WIDTH {
  SMALL = 'sm',
  MEDIUM = 'md',
  LARGE = 'lg',
}

interface StickyActionProps {
  label: string;
  onClick: (onChange) => any;
}

interface InnerFormSelectProps
  extends ExtendedWrappedFieldProps,
    Pick<CreatableProps<FormSelectOption>, 'onCreateOption'> {
  multi?: boolean;
  options: FormSelectOptions;
  clearable?: boolean;
  creatable?: boolean;
  minWidth?: MIN_WIDTH;
  virtualizeAbove?: number;
  createOnBlur?: boolean;
  hideCreatedOption?: boolean;
  stickyActions?: StickyActionProps[];
}

export type FormSelectProps = InnerFormSelectProps & StandardProps<ExtendedInputProps, never>;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    smSize: {
      minWidth: theme.spacing(15),
    },
    mdSize: {
      minWidth: theme.spacing(25),
    },
    lgSize: {
      minWidth: theme.spacing(50),
    },
    labelSize: {
      width: `calc(100% - ${theme.spacing(3)}px)`,
    },
    labelTypography: {
      lineHeight: 'normal',
    },
    popper: {
      zIndex: theme.zIndex.tooltip,
    },
    paper: {
      height: (({virtualized}) => (virtualized ? theme.spacing(5) * 7.5 : undefined)) as any,
      minWidth: (({width}) => width) as any,
    },
    iconIndicator: {
      cursor: ({isDisabled}: SelectProps<FormSelectOption>) => (isDisabled ? null : 'pointer'),
    },
    clearIndicator: {
      opacity: 0,
      '$control:hover &': {
        opacity: 0.5,
      },
      '$control:hover &:hover': {
        transition: theme.transitions.create('opacity', {duration: theme.transitions.duration.shortest}),
        opacity: 1,
      },
    },
    selectedOption: {
      fontWeight: 500,
    },
    noOptionsMessage: {
      padding: theme.spacing(1, 2),
    },
    control: {
      fontSize: theme.typography.pxToRem(16),
      pointerEvents: 'auto',
      '& input': {
        ...theme.typography.body2,
        fontFamily: 'inherit!important',
      },
    },
    inputComponent: {
      height: 'auto',
      fontSize: theme.typography.pxToRem(16),
      display: 'flex',
      paddingTop: theme.spacing(0.25),
      paddingBottom: theme.spacing(0.5),
    },
    typography: {
      fontSize: theme.typography.pxToRem(16),
    },
    placeholder: {
      position: 'absolute',
    },
    valueContainer: {
      display: 'flex',
      flex: 1,
      alignItems: 'center',
      overflow: 'hidden',
    },
    multiValueContainer: {
      flexWrap: 'wrap',
    },
    chip: {
      margin: theme.spacing(0.5, 0.25),
    },
    chipFocused: {
      backgroundColor: emphasize(
        theme.palette.type === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
        0.08
      ),
    },
    stickyItem: {
      paddingTop: 0,
      paddingBottom: 0,
      color: theme.palette.text.primary,
    },
  })
);

function NoOptionsMessage({innerProps, children, selectProps}: NoticeProps<FormSelectOption>) {
  const {noOptionsMessage, typography} = useStyles(selectProps);
  return (
    <Typography color="textSecondary" className={classNames(noOptionsMessage, typography)} {...innerProps}>
      {children}
    </Typography>
  );
}
function inputComponent({className, inputRef, selectProps, ...props}) {
  const {inputComponent} = useStyles(selectProps);
  return <div className={classNames(inputComponent, className)} ref={inputRef} {...props} />;
}
function Control({
  innerRef,
  children,
  selectProps,
  isFocused,
  innerProps,
}: ControlProps<FormSelectOption> & State<FormSelectOption>) {
  const {
    controlProps: {className, minWidth, label, ref, fullWidth, tooltipProps, ...inputProps},
    value,
    isDisabled,
  } = selectProps;
  const {smSize, mdSize, lgSize, labelSize, labelTypography, control} = useStyles(selectProps);
  return (
    <RootRef rootRef={ref}>
      <FormControl
        {...tooltipProps}
        className={classNames(className, control, {
          [smSize]: minWidth === MIN_WIDTH.SMALL,
          [mdSize]: minWidth === MIN_WIDTH.MEDIUM,
          [lgSize]: minWidth === MIN_WIDTH.LARGE,
        })}
        fullWidth={fullWidth}
        disabled={isDisabled}
      >
        {label ? (
          <InputLabel className={labelSize} shrink={isFocused || !_.isEmpty(value)}>
            <TypographyWithEllipsis className={labelTypography}>{label}</TypographyWithEllipsis>
          </InputLabel>
        ) : (
          undefined
        )}
        <Input
          disabled={isDisabled}
          inputComponent={inputComponent}
          inputRef={innerRef}
          inputProps={{
            'data-lpignore': true,
            children,
          }}
          {...inputProps}
          {...innerProps}
        />
      </FormControl>
    </RootRef>
  );
}
function Menu({children, innerProps, selectProps}: MenuProps<FormSelectOption>) {
  const {paper, stickyItem} = useStyles(selectProps);
  const {stickyActions, onChange, onMenuClose} = selectProps;
  return (
    <Paper square className={paper} {...innerProps}>
      {children}
      {_.map(stickyActions, ({label, onClick}) => (
        <ListSubheader
          disableGutters={true}
          component="div"
          {..._.isFunction(onClick) && {
            onClick: () => {
              onClick(onChange);
              onMenuClose();
            },
          }}
        >
          <Divider light />
          <ListItem button className={stickyItem}>
            <ListItemText primary={label} />
          </ListItem>
        </ListSubheader>
      ))}
    </Paper>
  );
}

function MenuList({children, innerRef}: MenuListProps) {
  const childrenArray = useMemo(() => React.Children.toArray(children), [children]);
  const theme = useTheme<Theme>();
  return (
    <RootRef rootRef={innerRef}>
      <AutoSizer>
        {({width, height}) => (
          <List
            rowCount={childrenArray.length}
            rowHeight={theme.spacing(5)}
            width={width}
            height={height}
            rowRenderer={({index, style}) => {
              return (
                <div key={index} style={style}>
                  {childrenArray[index]}
                </div>
              );
            }}
          />
        )}
      </AutoSizer>
    </RootRef>
  );
}

function Option({
  innerRef,
  children,
  innerProps,
  isFocused,
  isSelected,
  selectProps,
  data: {__isNew__, explanationText, icon: OptionIcon},
}: OptionProps<FormSelectOption>) {
  const {selectedOption} = useStyles(selectProps);
  const {hideCreatedOption} = selectProps;
  if (hideCreatedOption && __isNew__) {
    return null;
  }
  return (
    <MenuItem
      buttonRef={innerRef}
      selected={isFocused}
      component="div"
      className={classNames({[selectedOption]: isSelected})}
      {...innerProps}
    >
      {OptionIcon ? (
        <ListItemIcon>
          <OptionIcon />
        </ListItemIcon>
      ) : (
        undefined
      )}
      {explanationText ? <ListItemText primary={children} secondary={explanationText} /> : children}
    </MenuItem>
  );
}
function Placeholder({
  innerProps,
  children,
  isFocused,
  selectProps,
}: PlaceholderProps<FormSelectOption> & State<FormSelectOption>) {
  const {typography, placeholder} = useStyles(selectProps);
  const {
    controlProps: {label},
  } = selectProps;
  return isFocused || _.isEmpty(label) ? (
    <Typography className={classNames(typography, placeholder)} color="textSecondary" {...innerProps}>
      {children}
    </Typography>
  ) : null;
}

const TypographyWithEllipsis = withEllipsis()(Typography);
function SingleValue({innerProps, children, selectProps}: SingleValueProps<FormSelectOption>) {
  const {typography} = useStyles(selectProps);
  return (
    <TypographyWithEllipsis className={typography} {...innerProps}>
      {children}
    </TypographyWithEllipsis>
  );
}
function ValueContainer({selectProps, children}: ValueContainerProps<FormSelectOption>) {
  const {valueContainer, multiValueContainer} = useStyles(selectProps);
  const {isMulti} = selectProps;
  return (
    <div
      className={classNames(valueContainer, {
        [multiValueContainer]: isMulti,
      })}
    >
      {children}
    </div>
  );
}
function MenuPortal({appendTo, children, selectProps}: MenuPortalProps<FormSelectOption>) {
  const {popper} = useStyles(selectProps);
  return (
    <Popper placement="bottom-start" open anchorEl={appendTo} className={popper}>
      {children}
    </Popper>
  );
}
function MultiValue({
  data: {label},
  removeProps: {onClick: onDelete, ...deleteIconProps},
  isFocused,
  selectProps,
}: MultiValueProps<FormSelectOption>) {
  const {chip, chipFocused} = useStyles(selectProps);
  return (
    <Chip
      className={classNames(chip, {[chipFocused]: isFocused})}
      label={label}
      onDelete={onDelete}
      deleteIcon={<CancelIcon {...deleteIconProps} />}
    />
  );
}

function DropdownIndicator({innerProps, selectProps}: IndicatorProps<FormSelectOption>) {
  const {iconIndicator} = useStyles(selectProps);
  return <ArrowDropDownIcon className={iconIndicator} {...innerProps} />;
}

function ClearIndicator({innerProps, selectProps}: IndicatorProps<FormSelectOption>) {
  const {iconIndicator, clearIndicator} = useStyles(selectProps);
  return <CloseIcon className={classNames(iconIndicator, clearIndicator)} {...innerProps} />;
}

const components: Partial<SelectComponents<FormSelectOption>> = {
  Control,
  Menu,
  MenuList,
  MultiValue,
  MenuPortal,
  NoOptionsMessage,
  IndicatorSeparator: () => null,
  DropdownIndicator,
  ClearIndicator,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
};

const selectStyles: StylesConfig = {
  input: () => ({
    marginTop: 0,
    marginBottom: 0,
    paddingTop: 0,
    paddingBottom: 0,
  }),
};

const FormSelectInner = React.forwardRef<any, SelectProps<FormSelectOption>>((props, ref) => {
  const {controlProps} = props.internalProps;
  const ComponentType = props['ComponentType'];
  return (
    <ComponentType
      {...props.internalProps}
      controlProps={{
        ...controlProps,
        tooltipProps: _.omit(props, ['ComponentType', 'internalProps']),
        ref: useForkRef(controlProps.ref, ref),
      }}
    />
  );
});

const FormSelect: React.FunctionComponent<FormSelectProps> = ({
  showErrors,
  meta: {touched, error},
  options,
  explanationText,
  explanationTooltip,
  instructionText,
  instructionPosition,
  input: {value, onChange, onBlur, ...input},
  minWidth,
  className,
  label,
  multi,
  clearable,
  disabled,
  fullWidth,
  creatable,
  placeholder,
  virtualizeAbove,
  createOnBlur,
  hideCreatedOption,
  onCreateOption,
  autoFocus,
  id,
  disabledTooltip,
  stickyActions,
}) => {
  const formFieldProps = {
    showErrors,
    touched,
    error,
    explanationText,
    instructionText,
    instructionPosition,
    explanationTooltip,
    id,
    fieldName: input ? input.name : undefined,
    disabledTooltip: disabled ? disabledTooltip : undefined,
  };
  const virtualized = _.size(options) > virtualizeAbove && !stickyActions;
  const selectComponents = useMemo(() => {
    return virtualized ? components : _.omit(components, 'MenuList');
  }, [virtualized]);
  const [controlRef, setControlRef] = useState(null);
  options = useMemo(() => {
    return _.map(options, option => (option.label ? option : {...option, label: String(option.value)}));
  }, [options]);
  const optionsByValue = useMemo(() => _.keyBy(options, 'value'), [options]);
  const selected = useMemo(() => {
    const getSelectedOption = creatable
      ? value => {
          if (value) {
            if (optionsByValue[value]) {
              return optionsByValue[value];
            }
            return {value, label: value};
          }
          return value;
        }
      : value => optionsByValue[value];
    if (multi) {
      return _.map(value, getSelectedOption);
    }
    return getSelectedOption(value);
  }, [value, optionsByValue, multi]);
  const handleChange = useCallback(
    (value: FormSelectOption | FormSelectOptions) => {
      if (value) {
        return _.isArray(value) ? onChange(_.map(value, 'value')) : onChange(value.value);
      }
      return onChange(value);
    },
    [onChange, multi]
  );
  const handleBlur = useCallback(
    event => {
      if (createOnBlur && creatable && !multi) {
        return onBlur(event.target.value || value);
      }
      if (onBlur) {
        return onBlur(value);
      }
    },
    [onBlur, value]
  );
  const handleCreateOption = useCallback(
    onCreateOption
      ? createdValue => {
          onChange(multi ? [...value, createdValue] : createdValue);
          return onCreateOption(createdValue);
        }
      : undefined,
    [onChange, onCreateOption, multi, value]
  );

  let Component: React.ComponentType<SelectProps<FormSelectOption>> = creatable ? Creatable : Select;
  return (
    <FormField {...formFieldProps}>
      <FormSelectInner
        ComponentType={Component}
        internalProps={{
          value: selected,
          components: selectComponents,
          controlProps: {label, minWidth, id, ref: setControlRef, className, fullWidth},
          options: options,
          stickyActions: stickyActions,
          onChange: handleChange,
          onBlur: handleBlur,
          styles: selectStyles,
          menuPortalTarget: controlRef,
          isMulti: multi,
          isDisabled: disabled,
          placeholder: placeholder,
          virtualized: virtualized,
          isClearable: clearable,
          width: _.get(controlRef, 'clientWidth'),
          hideCreatedOption: hideCreatedOption,
          onCreateOption: handleCreateOption,
          autoFocus: autoFocus,
          ...input,
        }}
      />
    </FormField>
  );
};

FormSelect.defaultProps = {
  minWidth: MIN_WIDTH.MEDIUM,
  virtualizeAbove: 50,
};
export default FormSelect;
